import { useEffect, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import type L from 'leaflet';
import { synchronizeMapView, type GridMapType, type MapViewsState } from './mapConfig';
import { CADASTRAL_MAX_ZOOM } from 'constants/grid';
const centersMatch = (first?: [number, number], second?: [number, number]) =>
  Array.isArray(first) &&
  Array.isArray(second) &&
  first.length === 2 &&
  second.length === 2 &&
  first[0] === second[0] &&
  first[1] === second[1];

interface MapTrackerProps {
  mapType: GridMapType;
  setMapViews: Dispatch<SetStateAction<MapViewsState>>;
  isDrawingRef?: MutableRefObject<boolean> | null;
  viewCenter?: [number, number];
  viewZoom?: number;
  resizeSignal?: boolean;
  focusLayerRef?: MutableRefObject<L.Polygon | null> | null;
}

const MapTracker = ({
  mapType,
  setMapViews,
  isDrawingRef,
  viewCenter,
  viewZoom,
  resizeSignal,
  focusLayerRef,
}: MapTrackerProps) => {
  const map = useMap();
  useEffect(() => {
    if (isDrawingRef?.current) {
      return;
    }
    if (viewCenter?.length === 2 && Number.isFinite(viewZoom)) {
      const currentCenter = map.getCenter();
      const currentZoom = map.getZoom();
      const nextCenter: [number, number] = [currentCenter.lat, currentCenter.lng];

      if (centersMatch(nextCenter, viewCenter) && currentZoom === viewZoom) {
        return;
      }

      map.setView(viewCenter, viewZoom, { animate: false });
    }
  }, [map, isDrawingRef, viewCenter, viewZoom]);

  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return undefined;
    }
    if (isDrawingRef?.current) {
      return undefined;
    }
    let rafId = null;
    rafId = requestAnimationFrame(() => {
      map.invalidateSize({ animate: false });
      const focusLayer = focusLayerRef?.current;
      const focusBounds = focusLayer?.getBounds?.();
      if (resizeSignal && focusBounds?.isValid?.()) {
        map.fitBounds(focusBounds, {
          animate: false,
          paddingTopLeft: [24, 24],
          paddingBottomRight: [24, 24],
          maxZoom: map.getZoom(),
        });
      }
      const center = map.getCenter();
      const zoomLevel = map.getZoom();
      setMapViews((prev) => synchronizeMapView(prev, mapType, { center: [center.lat, center.lng], zoom: zoomLevel }));
    });
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [map, mapType, isDrawingRef, resizeSignal, setMapViews, focusLayerRef]);

  useEffect(() => {
    const syncDoubleClickZoomState = () => {
      if (!map?.doubleClickZoom) {
        return;
      }
      if (mapType !== 'cadastral') {
        map.doubleClickZoom.enable();
        return;
      }
      if (map.getZoom() >= CADASTRAL_MAX_ZOOM) {
        map.doubleClickZoom.disable();
      } else {
        map.doubleClickZoom.enable();
      }
    };

    syncDoubleClickZoomState();
    map.on('zoomend', syncDoubleClickZoomState);
    return () => {
      map.off('zoomend', syncDoubleClickZoomState);
    };
  }, [map, mapType]);

  useMapEvents({
    zoomend: () => {
      if (isDrawingRef?.current) {
        return;
      }
      const zoomLevel = map.getZoom();
      setMapViews((prev) => synchronizeMapView(prev, mapType, { zoom: zoomLevel }));
    },
    moveend: () => {
      if (isDrawingRef?.current) {
        return;
      }
      const center = map.getCenter();
      setMapViews((prev) => synchronizeMapView(prev, mapType, { center: [center.lat, center.lng] }));
    },
  });
  return null;
};

export default MapTracker;
